/**
 * Resolves the name a Signify product gives its Products record, and says why
 * when it cannot. Consumers: `useCreateLineItemFromSignify`, which refuses the
 * insert on a failure, and `useSignifyProductModal`, which previews it.
 *
 * The engine stops at the first source that is not `null`, `undefined` or `''`,
 * so a number, a `false` or a whitespace-only string would end the fallback
 * list. A name must be text, so this reads the matched rule's own sources in
 * order and takes the first non-blank string, which is the `recordNamePriority`
 * loop the migration folded into that single rule. Both reads go through the
 * engine's `readSignifyResponsePath`: one reader, so a source this scan can
 * read is exactly a source the engine could have chosen, and the scan
 * reproduces the old loop's order over the sources the engine sees. One
 * deliberate difference from that loop: a configured path that indexes into a
 * primitive (`product.name.0`) yields no name here where the loop returned a
 * character — a misconfigured path, which the refusal surfaces. Only the
 * matched rule is read: rules still never fall through.
 *
 * Every outcome but `noValue` carries the source it read, so the modal's
 * Source column names a response path or a constant rather than the word
 * "Name": on `resolved` it is the scanned source that won, on `notText` it is
 * the source the engine itself chose.
 */
import {
  readSignifyResponsePath,
  resolveSignifyFieldValues,
  SIGNIFY_RECORD_NAME_DESTINATION,
  type SignifyFieldMappings,
  type SignifyValueSource,
} from '#types';

export type SignifyRecordName =
  | { status: 'resolved'; name: string; source: SignifyValueSource }
  /** `rulePosition` is the rule's 1-based position, as the settings editor numbers it. */
  | { status: 'notText'; valueSummary: string; rulePosition: number; source: SignifyValueSource }
  | { status: 'noValue' };

const summarizeValue = (value: unknown): string => {
  if (typeof value === 'string') return `"${value}"`;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return `a value of type ${typeof value}`;
};

const readSource = (source: SignifyValueSource, response: Record<string, unknown>): unknown =>
  source.type === 'constant' ? source.value : readSignifyResponsePath(response, source.path);

export const resolveSignifyRecordName = (params: {
  fieldMappings: SignifyFieldMappings;
  response: Record<string, unknown>;
}): SignifyRecordName => {
  const { fieldMappings, response } = params;
  const rules = fieldMappings[SIGNIFY_RECORD_NAME_DESTINATION] ?? [];

  const resolved = resolveSignifyFieldValues({
    fieldMappings: { [SIGNIFY_RECORD_NAME_DESTINATION]: rules },
    response,
  })[SIGNIFY_RECORD_NAME_DESTINATION];

  if (!resolved) return { status: 'noValue' };

  const named = (rules[resolved.ruleIndex]?.sources ?? [])
    .map((source) => ({ source, value: readSource(source, response) }))
    .find(
      (read): read is { source: SignifyValueSource; value: string } =>
        typeof read.value === 'string' && read.value.trim() !== ''
    );

  return named === undefined
    ? {
        status: 'notText',
        valueSummary: summarizeValue(resolved.value),
        rulePosition: resolved.ruleIndex + 1,
        source: resolved.source,
      }
    : { status: 'resolved', name: named.value.trim(), source: named.source };
};
